import React, { useMemo, useRef } from 'react';
import imageUrlBuilder from '@sanity/image-url';
import { Grid } from '@tmap/mmm-style-guide/src/Grid';
import { Typography } from '@tmap/mmm-style-guide/src/Typography';
import { styled } from '@tmap/mmm-style-guide/src/styled';
import locationLabelText from '@tmap/mmm-core/locationLabelText';
import { toKey, types } from '@tmap/mmm-core/entityKey';
import LinkCard from '../linkCard';
import OfferValueItem from '../offers/offerValueItem';
import findImpressionFeature from '../../lib/findImpressionFeature';
import { getTagPills } from '../../lib/tags';
import getPrimaryIncentiveImage from '../../lib/getPrimaryIncentiveImage';
import useImpressions, { ImpressionTracker } from '../../hooks/useImpressions';
import client from '../../client';
import IncentiveCTAButton from './incentiveCTAButton';
import IncompleteTasksCTA from './incompleteTasksCTA';

const builder = imageUrlBuilder(client);

const incentiveImgStyles = () => ({
  width: '100%',
  maxHeight: '550px',
  objectFit: 'cover',
  borderRadius: '8px',
});
const StyledIncentivePicture = styled('picture')(incentiveImgStyles);
const StyledIncentiveImg = styled('img')(incentiveImgStyles);

const FlexColumn = styled('div')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  width: '100%',
  height: '100%',
  padding: theme.spacing(2),
  [theme.breakpoints.down('md')]: {
    padding: theme.spacing(1),
  },
  marginBottom: theme.spacing(6),
}));

const ApplyWrapper = styled('div')(() => ({
  position: 'absolute',
  bottom: 0,
  left: 0,
  right: 0,
  padding: 'inherit',
}));

const SpacedGrid = styled(Grid)(({ theme }) => ({
  marginBottom: theme.spacing(1),
}));

function IncentiveCardDetailed(props) {
  const {
    incentive,
    shouldLazyLoad,
    lazyProps,
    placementId,
    pageId,
    maxIncentives = 5,
    ...cardProps
  } = props;

  const incentiveUrl = `/get-paid/${encodeURIComponent(incentive.slug.current)}`;
  const primaryImage = getPrimaryIncentiveImage(incentive);
  const altText = `Get paid to live in ${locationLabelText(incentive.locationText, incentive.regionText)}`;

  const impressionMeta = useMemo(() => ({ pageId }), [pageId]);
  const { trackView, trackClick } = useImpressions(
    toKey(types.INCENTIVE, incentive._id),
    placementId,
    impressionMeta,
  );

  const baseElement = useRef();
  const favoriteTarget = useRef();
  const applyTarget = useRef();

  const handleImpressionClick = (event) => {
    const elementsToTrack = new Map([
      [favoriteTarget.current, 'favorite'],
      [applyTarget.current, 'apply'],
      [baseElement.current, 'base'],
    ]);

    const featureName = findImpressionFeature(event, elementsToTrack);
    trackClick({ feature: featureName });
  };

  const incentiveValues = useMemo(() => {
    if (incentive?.values?.length > maxIncentives) {
      const excessIncentives = incentive.values.length - maxIncentives + 1;
      const plusMoreBenefits = {
        valueDescription: `Plus ${excessIncentives} more benefits`, valueType: 'other',
      };
      return [...(incentive.values.slice(0, maxIncentives - 1)), plusMoreBenefits];
    }
    return incentive?.values || [];
  }, [incentive?.values, maxIncentives]);

  return (
    <ImpressionTracker
      trackView={trackView}
      onClickCapture={handleImpressionClick}
      ref={baseElement}
    >
      <LinkCard href={incentiveUrl} alt={altText} variant='raised' sx={{ height: '100%' }} {...cardProps}>
        <LinkCard.ImageWrapper>
          <LinkCard.Overlays sx={{ padding: 1 }}>
            <LinkCard.PillBar>
              {incentive.tags && getTagPills(incentive.tags).map(({ label, icon, iconColor }, i) => <LinkCard.Pill key={`inc-${incentive._id}-pill-${i}`} label={label} icon={icon} iconColor={iconColor} />)}
            </LinkCard.PillBar>
            <LinkCard.FavoriteButton entity={incentive} ref={favoriteTarget} twoToneHeart />
          </LinkCard.Overlays>
          <LinkCard.Image borderRadius='8px' dimTop shouldLazyLoad={shouldLazyLoad} lazyProps={lazyProps}>
            <StyledIncentivePicture>
              <source
                srcSet={builder.image(primaryImage).format('webp').width(500).height(200)
                  .url()}
                width={500}
                height={200}
                media='(min-width: 1200px)'
              />
              <StyledIncentiveImg
                src={builder.image(primaryImage).width(330).height(200).url()}
                width={330}
                height={200}
                alt={altText}
              />
            </StyledIncentivePicture>
          </LinkCard.Image>
        </LinkCard.ImageWrapper>
        <LinkCard.Footer>
          <FlexColumn>
            <SpacedGrid container spacing={1}>
              <Grid item xs>
                <Typography variant='body1' fontWeight={600}>{incentive.title || 'Total Value'}</Typography>
              </Grid>
              <Grid item text='right'>
                <Typography variant='body1' color='secondary'>
                  {incentive.totalValue ? `$${new Intl.NumberFormat().format(incentive.totalValue)}` : ''}
                </Typography>
              </Grid>
            </SpacedGrid>
            <SpacedGrid container spacing={2}>
              {incentiveValues?.length > 0
                && incentiveValues?.map((incentiveValue, i) => (
                  <Grid item xs={12} key={`iv-${i}`}>
                    <OfferValueItem
                      offerValue={incentiveValue}
                      showIcon={false}
                      valueProps={{ variant: 'body2' }}
                      disableLinks
                    />
                  </Grid>
                ))}
              <IncompleteTasksCTA incentive={incentive} sx={{ mt: 2, ml: 2, width: '100%' }} />
            </SpacedGrid>
            <ApplyWrapper>
              <IncentiveCTAButton
                ref={applyTarget}
                incentive={incentive}
                variant='contained'
                color='inverted'
                disableElevation
              />
            </ApplyWrapper>
          </FlexColumn>
        </LinkCard.Footer>
      </LinkCard>
    </ImpressionTracker>
  );
}

export default IncentiveCardDetailed;
